import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Check, Sparkles, Eye, EyeOff, ImageOff, Printer, Share2, Tag, FolderTree } from "lucide-react";
import LabelPrintModal from "./LabelPrintModal";
import ImageUpload, { resolveImageUrl } from "../../components/ImageUpload";
import VideoUpload from "../../components/VideoUpload";
import MarkdownEditor from "../../components/MarkdownEditor";
import CopyLinkButton from "../../components/CopyLinkButton";
import ShareBuilder from "./ShareBuilder";
import BulkDescribeModal from "./BulkDescribeModal";
import HelpVideoButton from "../../components/HelpVideoButton";
import { useAuth } from "../../context/AuthContext";
import { productShareUrl } from "../../lib/shareLink";
import { ProductArticles } from "../../components/admin/ProductArticles";
import { ProductQuickSearch } from "../../components/admin/ProductQuickSearch";
import { useAvailableProducts } from "../../components/admin/useAvailableProducts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const inputStyle = { background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "10px 12px", fontFamily: "Manrope", fontSize: "14px", outline: "none", width: "100%", color: "#0A0A0A" };
const labelStyle = { fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px", display: "block" };

function ProductForm({ product, onSave, onClose }) {
  const initial = product
    ? { ...product, sizes: Array.isArray(product.sizes) ? product.sizes.join(", ") : (product.sizes || ""),
        images: Array.isArray(product.images) ? product.images : [],
        videos: Array.isArray(product.videos) ? product.videos : [],
        review_videos: Array.isArray(product.review_videos) ? product.review_videos : [] }
    : { name: "", category: "", subcategory: "", brand: "", description: "", material: "", price: "", old_price: "", discount: "",
        sizes: "", images: [], videos: [], review_videos: [], sku: "", our_sku: "", is_new: false, is_active: true, in_stock: true };
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [categoryTree, setCategoryTree] = useState([]);
  const [brands, setBrands] = useState([]);
  const [skuGenerating, setSkuGenerating] = useState(false);
  const [filler, setFiller] = useState("");
  const [materialHint, setMaterialHint] = useState("");
  const [furHint, setFurHint] = useState("");
  // Варианты наполнителей/материалов/меха теперь живут в справочнике
  // (Категории → «Подсказки для ИИ»), а не в коде.
  const [hintKinds, setHintKinds] = useState([]);
  const [hintOptions, setHintOptions] = useState({});
  const [showArrival, setShowArrival] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  const { user } = useAuth();
  // Оприходование и закупочные цены — только владелец и бухгалтер;
  // печать ценника доступна всем.
  const canReceive = ["admin", "accountant"].includes(user?.role);

  const openLabelFromForm = async () => {
    if (!form.our_sku && product?.id) {
      setLabelBusy(true);
      try {
        const r = await axios.post(`${API}/admin/products/${product.id}/generate-sku`, {},
          { withCredentials: true });
        if (r.data?.our_sku) setForm(f => ({ ...f, our_sku: r.data.our_sku }));
      } catch { /* печатаем как есть */ }
      setLabelBusy(false);
    }
    setShowLabel(true);
  };
  // Track which field the user last edited so we don't fight their input.
  const lastEdited = useRef(null);
  // Prevent the auto-generate effect from firing more than once per modal open.
  const autoTried = useRef(false);
  // Dirty flag — flipped on any user-driven mutation. Used to prompt confirm
  // when closing via X (accidental clicks shouldn't wipe the whole form).
  const dirtyRef = useRef(false);

  const set = (k, v) => { dirtyRef.current = true; setForm(f => ({ ...f, [k]: v })); };

  const requestClose = () => {
    if (!dirtyRef.current) { onClose(); return; }
    if (window.confirm("Закрыть без сохранения? Введённые данные будут потеряны.")) {
      onClose();
    }
  };

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategoryTree(r.data || [])).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/ai-hints`, { withCredentials: true })
      .then(r => { setHintKinds(r.data?.kinds || []); setHintOptions(r.data?.items || {}); })
      .catch(() => {});
  }, []);

  const hintValue = (kind) => (kind === "filler" ? filler : kind === "material" ? materialHint : furHint);

  // Выбор подсказки сразу пишется в поле «Материал / состав» товара —
  // чтобы состав попадал в карточку, а не только в текст описания.
  const applyHint = (kind, value) => {
    if (kind === "filler") setFiller(value);
    else if (kind === "material") setMaterialHint(value);
    else setFurHint(value);
    if (value) set("material", value);
  };

  const currentTop = categoryTree.find(c => c.name === form.category);
  const subcategoryOptions = currentTop?.subcategories || [];

  // Convert "Norka skandinavskaya" → real material name; just take the first product image (saves tokens)
  const runAiDescribe = async (override) => {
    const firstImg = (override?.images || form.images)?.[0];
    if (!firstImg) {
      setAiError("Сначала загрузите хотя бы одно фото");
      return null;
    }
    setAiError("");
    setAiLoading(true);
    try {
      const payload = {
        image_url: firstImg,
        name: form.name || undefined,
        category: form.category || undefined,
        brand: form.brand || undefined,
        price: form.price ? parseFloat(form.price) : undefined,
        filler: filler || undefined,
        material: materialHint || undefined,
        fur: furHint || undefined,
      };
      const res = await axios.post(`${API}/admin/products/ai-describe`, payload, { withCredentials: true });
      const d = res.data || {};
      const richDesc = [d.description, d.material && `Материал: ${d.material}.`, d.color && `Цвет: ${d.color}.`]
        .filter(Boolean).join(" ");
      setForm(f => ({
        ...f,
        name: f.name?.trim() ? f.name : (d.name || f.name),
        description: f.description?.trim() ? f.description : richDesc,
        material: f.material?.trim() ? f.material : (d.material || ""),
        // Only override category if it's empty AND ai picked a valid existing category
        category: f.category || (categoryTree.some(c => c.name === d.category) ? d.category : f.category),
        subcategory: f.subcategory || (d.subcategory || ""),
        brand: f.brand || (brands.some(b => b.name === d.brand) ? d.brand : f.brand),
      }));
      return d;
    } catch (err) {
      setAiError(err.response?.data?.detail || "Не удалось сгенерировать описание");
      return null;
    } finally { setAiLoading(false); }
  };

  // Auto-trigger: when user uploaded first photo AND description is empty AND not yet attempted.
  useEffect(() => {
    if (autoTried.current) return;
    if ((form.images?.length || 0) === 0) return;
    if (form.description?.trim()) return;
    if (aiLoading) return;
    autoTried.current = true;
    runAiDescribe();
  }, [form.images, form.description, aiLoading]);

  // Auto-calculate price ↔ discount synchronously — keep the math in the change handler
  // to avoid setState-in-useEffect.
  const setTracked = (k) => (v) => {
    lastEdited.current = k;
    setForm(f => {
      const next = { ...f, [k]: v };
      const old_p = parseFloat(next.old_price);
      const price = parseFloat(next.price);
      const disc = parseFloat(next.discount);
      if (k === "discount" && !isNaN(old_p) && old_p > 0 && !isNaN(disc) && disc >= 0 && disc <= 99) {
        next.price = String(Math.round(old_p * (1 - disc / 100)));
      } else if (k === "price" && !isNaN(old_p) && old_p > 0 && !isNaN(price) && price > 0 && price < old_p) {
        next.discount = String(Math.round((1 - price / old_p) * 100));
      } else if (k === "old_price" && !isNaN(old_p) && old_p > 0 && !isNaN(disc) && disc > 0) {
        next.price = String(Math.round(old_p * (1 - disc / 100)));
      }
      return next;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price) || 0,
        old_price: form.old_price ? parseFloat(form.old_price) : null,
        discount: form.discount ? parseInt(form.discount) : null,
        sizes: typeof form.sizes === "string" ? form.sizes.split(",").map(s => s.trim()).filter(Boolean) : form.sizes,
        images: Array.isArray(form.images) ? form.images : [],
      };
      if (product?.id) {
        await axios.put(`${API}/products/${product.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/products`, data, { withCredentials: true });
      }
      onSave();
    } catch (err) {
      alert(err.response?.data?.detail || "Ошибка сохранения");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px", overflowY: "auto" }}>
      <div className="light-section" style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "640px", padding: "24px", marginTop: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400 }}>{product ? "Редактировать товар" : "Новый товар"}</h3>
          <button data-testid="product-form-close-x" onClick={requestClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={20} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Название</label>
            <input data-testid="product-name-input" value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle} placeholder="Название товара" />
          </div>
          <div>
            <label style={labelStyle}>Категория</label>
            <select data-testid="product-category-select" value={form.category} onChange={e => { set("category", e.target.value); set("subcategory", ""); setFiller(""); setMaterialHint(""); setFurHint(""); }} style={inputStyle}>
              <option value="">Выберите...</option>
              {categoryTree.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          {subcategoryOptions.length > 0 && (
            <div>
              <label style={labelStyle}>Подкатегория</label>
              <select data-testid="product-subcategory-select" value={form.subcategory || ""} onChange={e => set("subcategory", e.target.value)} style={inputStyle}>
                <option value="">— Все —</option>
                {subcategoryOptions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={labelStyle}>Бренд</label>
            <select data-testid="product-brand-select" value={form.brand || ""} onChange={e => set("brand", e.target.value)} style={inputStyle}>
              <option value="">— Без бренда —</option>
              {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1/-1", background: "#fafaf9", border: "1px solid #eee", borderRadius: "8px", padding: "12px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={labelStyle}>Наш SKU (внутренний артикул)</label>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input data-testid="product-our-sku-input" value={form.our_sku || ""} readOnly
                         style={{ ...inputStyle, background: form.our_sku ? "#f5f5f4" : "white",
                                  fontFamily: "monospace", fontWeight: 600, letterSpacing: "0.02em" }}
                         placeholder="—" />
                  {!form.our_sku && (
                    <button type="button" data-testid="product-generate-sku-btn"
                            disabled={skuGenerating || !product?.id || !form.brand}
                            title={!product?.id ? "Сначала сохраните товар" : (!form.brand ? "Сначала выберите бренд" : "Сгенерировать SKU по бренду и году")}
                            onClick={async () => {
                              if (!product?.id) return;
                              setSkuGenerating(true);
                              try {
                                const r = await axios.post(`${API}/admin/products/${product.id}/generate-sku`, {}, { withCredentials: true });
                                if (r.data?.our_sku) set("our_sku", r.data.our_sku);
                              } catch (e) {
                                alert(e.response?.data?.detail || "Не удалось сгенерировать SKU");
                              } finally { setSkuGenerating(false); }
                            }}
                            style={{ background: "linear-gradient(135deg,#1a1a1a,#2a2a2a)", color: "#D4B878",
                                     border: "1px solid #C5A059", borderRadius: "4px",
                                     padding: "0 12px", cursor: skuGenerating ? "wait" : "pointer",
                                     fontFamily: "Manrope", fontSize: "11px", fontWeight: 700,
                                     letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap",
                                     opacity: (!product?.id || !form.brand) ? 0.5 : 1 }}>
                      {skuGenerating ? "…" : "Сгенерировать"}
                    </button>
                  )}
                </div>
                <div style={{ fontFamily: "Manrope", fontSize: "10.5px", color: "#8a8a89", marginTop: "4px", lineHeight: 1.4 }}>
                  Формат: <b>BB-NN-YY</b> · присваивается по бренду и году (например, NS-01-26).
                </div>
              </div>
              <div>
                <label style={labelStyle}>Артикул завода / штрихкод</label>
                <input value={form.sku || ""} onChange={e => set("sku", e.target.value)} style={inputStyle} placeholder="2110086000004" />
                <div style={{ fontFamily: "Manrope", fontSize: "10.5px", color: "#8a8a89", marginTop: "4px" }}>
                  Приходит от поставщика / уже нанесённая этикетка.
                </div>
              </div>
            </div>
            {product?.id && (
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                {canReceive && form.our_sku && (
                  <button type="button" data-testid="product-quick-arrival-btn"
                        onClick={() => setShowArrival(true)}
                        style={{ flex: 1, background: "linear-gradient(135deg,#C5A059,#D4B878)",
                                 color: "#0A0A0A", border: "none", borderRadius: "4px", padding: "10px 14px",
                                 cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                                 letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  Оприходовать
                </button>
                )}
                <button type="button" data-testid="product-print-label-btn"
                        onClick={openLabelFromForm} disabled={labelBusy}
                        style={{ flex: 1, background: "white", color: "#0A0A0A",
                                 border: "1px solid #C5A059", borderRadius: "4px", padding: "10px 14px",
                                 cursor: labelBusy ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                                 letterSpacing: "0.1em", textTransform: "uppercase",
                                 display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  <Printer size={13} /> {labelBusy ? "Готовим…" : "Печать этикетки"}
                </button>
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Старая цена ₸</label>
            <input data-testid="product-old-price-input" type="number" value={form.old_price || ""}
              onChange={e => setTracked("old_price")(e.target.value)} style={inputStyle} placeholder="850 000" />
          </div>
          <div>
            <label style={labelStyle}>Скидка %</label>
            <input data-testid="product-discount-input" type="number" min="0" max="99" value={form.discount || ""}
              onChange={e => setTracked("discount")(e.target.value)} style={inputStyle} placeholder="0" />
          </div>
          <div style={{ gridColumn: "1/-1", background: form.discount && form.old_price ? "rgba(197,160,89,0.08)" : "transparent", borderRadius: "6px", padding: form.discount && form.old_price ? "10px 12px" : "0", border: form.discount && form.old_price ? "1px solid rgba(197,160,89,0.2)" : "none", transition: "all 0.2s" }}>
            <label style={labelStyle}>Итоговая цена ₸ <span style={{ color: "#C5A059", fontWeight: 500, letterSpacing: 0, textTransform: "none" }}>(пересчитывается автоматически)</span></label>
            <input data-testid="product-price-input" type="number" value={form.price}
              onChange={e => setTracked("price")(e.target.value)} style={{ ...inputStyle, fontWeight: 700, fontSize: "16px", color: "#C5A059" }} placeholder="Итоговая цена" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Размеры (через запятую)</label>
            <input value={form.sizes} onChange={e => set("sizes", e.target.value)} style={inputStyle} placeholder="42, 44, 46" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Материал / состав</label>
            <input data-testid="product-material-input" value={form.material || ""}
              onChange={e => set("material", e.target.value)} style={inputStyle}
              placeholder="100% кашемир / Наполнитель 100% гусиный пух" />
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>
              Заполняется автоматически при выборе подсказки для ИИ. Список значений — в разделе «Категории».
            </div>
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Описание</label>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {hintKinds.filter(k => (hintOptions[k.key] || []).length > 0).map(k => (
                  <select key={k.key} data-testid={`ai-${k.key}-select`} value={hintValue(k.key)}
                    onChange={e => applyHint(k.key, e.target.value)}
                    title={`Подсказать ИИ точное значение: ${k.label}`}
                    style={{ background: "white", border: "1px solid #C5A059", borderRadius: "999px",
                             padding: "4px 8px", fontFamily: "Manrope", fontSize: "10.5px", fontWeight: 600,
                             color: hintValue(k.key) ? "#0A0A0A" : "#9ca3af", cursor: "pointer", outline: "none",
                             maxWidth: "180px" }}>
                    <option value="">{k.label}…</option>
                    {(hintOptions[k.key] || []).map(o => (
                      <option key={o.id} value={o.value}>{o.value}</option>
                    ))}
                  </select>
                ))}
                <button type="button" data-testid="ai-describe-btn" onClick={() => runAiDescribe()} disabled={aiLoading || (form.images?.length || 0) === 0}
                  style={{ background: aiLoading ? "#f3f4f6" : "linear-gradient(135deg, #1a1a1a, #2a2a2a)", color: aiLoading ? "#9ca3af" : "#D4B878", border: "1px solid #C5A059", borderRadius: "999px", padding: "4px 10px 4px 8px", cursor: aiLoading ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "4px", opacity: (form.images?.length || 0) === 0 ? 0.5 : 1 }}>
                  <Sparkles size={11} /> {aiLoading ? "Генерируем…" : "ИИ-описание"}
                </button>
              </div>
            </div>
            <MarkdownEditor
              value={form.description}
              onChange={v => set("description", v)}
              rows={5}
              testId="product-description-input"
              placeholder={aiLoading ? "ИИ анализирует фото и пишет описание…" : "Сдержанный люкс: тип, материал, силуэт, ключевая деталь"}
              style={{ opacity: aiLoading ? 0.6 : 1 }}
            />
            {aiError && <div style={{ marginTop: "4px", fontFamily: "Manrope", fontSize: "11px", color: "#dc2626" }}>{aiError}</div>}
            {!aiError && (form.images?.length || 0) > 0 && !form.description && !aiLoading && <div style={{ marginTop: "4px", fontFamily: "Manrope", fontSize: "11px", color: "#9ca3af" }}>Совет: нажмите «ИИ-описание» — мы заполним описание, материал и категорию по фото.</div>}
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <ImageUpload value={form.images} onChange={imgs => { autoTried.current = false; set("images", imgs); }} maxImages={8} testId="product-images" label="Фотографии товара" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <VideoUpload value={form.videos} onChange={vs => set("videos", vs)} maxVideos={3} testId="product-videos" />
          </div>
          <div style={{ gridColumn: "1/-1", background: "#fafaf9", border: "1px solid #f0e7d8", borderRadius: "8px", padding: "12px" }}>
            <VideoUpload value={form.review_videos} onChange={vs => set("review_videos", vs)} maxVideos={3}
              testId="product-review-videos" label="Видео-обзор от консультанта"
              hint="Обзор товара, снятый консультантом в бутике — на себе или на манекене. Показывается клиентам в карточке товара." />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="checkbox" id="is_new" checked={form.is_new} onChange={e => set("is_new", e.target.checked)} />
            <label htmlFor="is_new" style={{ fontFamily: "Manrope", fontSize: "13px" }}>Новинка</label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="checkbox" id="in_stock" checked={form.in_stock} onChange={e => set("in_stock", e.target.checked)} />
            <label htmlFor="in_stock" style={{ fontFamily: "Manrope", fontSize: "13px" }}>В наличии</label>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px" }}>Отмена</button>
          <button data-testid="save-product-btn" onClick={handleSave} disabled={loading}
            style={{ flex: 2, background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Check size={15} />{loading ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>
      {showArrival && product?.id && (
        <QuickArrivalModal product={{ ...product, our_sku: form.our_sku }}
                           onClose={() => setShowArrival(false)}
                           onDone={() => { setShowArrival(false); onSave(); }} />
      )}
      {showLabel && product?.id && (
        <LabelPrintModal
          product={{
            ...product,
            our_sku: form.our_sku,
            sku: form.sku,
            name: form.name,
            price: form.price,
            old_price: form.old_price,
            discount: form.discount,
          }}
          onClose={() => setShowLabel(false)}
        />
      )}
    </div>
  );
}


/**
 * QuickArrivalModal — one-shot "goods incoming" flow launched from the
 * product card. Behind the scenes it hits `/admin/products/{id}/quick-arrival`
 * which creates + posts an arrival, generates the SKU if missing, then bumps
 * stock. Keep the UI minimal: warehouse, invoice header, per-size qty, cost,
 * FX rate, extra expenses (доставка / пошлина).
 */
function QuickArrivalModal({ product, onClose, onDone }) {
  const [warehouses, setWarehouses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const dirtyRef = useRef(false);
  const productSizes = Array.isArray(product.sizes)
    ? product.sizes
    : (typeof product.sizes === "string" ? product.sizes.split(",").map(s => s.trim()).filter(Boolean) : []);
  const initialSizes = productSizes.length ? productSizes.map(s => ({ size: s, qty: 0 })) : [{ size: "", qty: 0 }];

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    warehouse_id: "",
    invoice_number: "",
    invoice_date: today,
    currency: "EUR",
    exchange_rate_to_kzt: "",
    cost_price_original: "",
    sizes_qty: initialSizes,
    expenses: [{ name: "Доставка на партию", amount_kzt: "" }],
    notes: "",
  });
  const set = (k, v) => { dirtyRef.current = true; setForm(f => ({ ...f, [k]: v })); };

  const requestClose = () => {
    if (!dirtyRef.current) { onClose(); return; }
    if (window.confirm("Закрыть без сохранения? Введённые данные будут потеряны.")) {
      onClose();
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/warehouses`, { withCredentials: true })
      .then(r => {
        setWarehouses(r.data || []);
        const def = (r.data || []).find(w => w.is_default) || (r.data || [])[0];
        if (def) set("warehouse_id", def.id);
      }).catch(() => {});
  }, []);

  const totalQty = form.sizes_qty.reduce((s, r) => s + (parseInt(r.qty) || 0), 0);
  const rate = parseFloat(form.exchange_rate_to_kzt) || 0;
  const unitCostKzt = form.currency === "KZT" ? (parseFloat(form.cost_price_original) || 0)
                    : (parseFloat(form.cost_price_original) || 0) * rate;
  const expensesTotalKzt = form.expenses.reduce((s, e) => s + (parseFloat(e.amount_kzt) || 0), 0);
  const arrivalTotalKzt = unitCostKzt * totalQty + expensesTotalKzt;

  const submit = async () => {
    setErr("");
    if (!form.warehouse_id) { setErr("Выберите склад"); return; }
    if (!form.invoice_number.trim()) { setErr("Укажите номер инвойса"); return; }
    if (form.currency !== "KZT" && rate <= 0) { setErr("Укажите курс к тенге"); return; }
    if (totalQty <= 0) { setErr("Укажите количество хотя бы для одного размера"); return; }

    setSaving(true);
    try {
      const body = {
        warehouse_id: form.warehouse_id,
        invoice_number: form.invoice_number.trim(),
        invoice_date: form.invoice_date,
        currency: form.currency,
        exchange_rate_to_kzt: rate || 1,
        cost_price_original: parseFloat(form.cost_price_original) || 0,
        sizes_qty: form.sizes_qty
          .filter(r => (parseInt(r.qty) || 0) > 0)
          .map(r => ({ size: r.size || null, qty: parseInt(r.qty) })),
        expenses: form.expenses
          .filter(e => (parseFloat(e.amount_kzt) || 0) > 0)
          .map(e => ({ name: e.name.trim() || "Расход", amount_kzt: parseFloat(e.amount_kzt) })),
        notes: form.notes,
      };
      const r = await axios.post(`${API}/admin/products/${product.id}/quick-arrival`, body, { withCredentials: true });
      alert(`Поступление проведено!\nSKU: ${r.data.our_sku}\nИтого: ${Math.round(r.data.total_cost_kzt).toLocaleString("ru-RU")} ₸\nКоличество: ${totalQty} шт.`);
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка проведения");
    }
    setSaving(false);
  };

  const updateSize = (idx, key, val) => { dirtyRef.current = true; setForm(f => ({ ...f, sizes_qty: f.sizes_qty.map((r, i) => i === idx ? { ...r, [key]: val } : r) })); };
  const addSizeRow = () => { dirtyRef.current = true; setForm(f => ({ ...f, sizes_qty: [...f.sizes_qty, { size: "", qty: 0 }] })); };
  const updateExp = (idx, key, val) => { dirtyRef.current = true; setForm(f => ({ ...f, expenses: f.expenses.map((r, i) => i === idx ? { ...r, [key]: val } : r) })); };
  const addExpRow = () => { dirtyRef.current = true; setForm(f => ({ ...f, expenses: [...f.expenses, { name: "", amount_kzt: "" }] })); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px", overflowY: "auto" }}>
      <div data-testid="quick-arrival-modal" onClick={e => e.stopPropagation()}
           style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "560px", padding: "22px", marginTop: "20px", color: "#0A0A0A" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
          <div>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400, margin: 0 }}>Поступление товара</h3>
            <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
              SKU: <b style={{ fontFamily: "monospace" }}>{product.our_sku || "—"}</b> · {product.name}
            </div>
          </div>
          <button data-testid="qa-close-x" onClick={requestClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={20} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <div>
            <label style={labelStyle}>Склад *</label>
            <select data-testid="qa-warehouse-select" value={form.warehouse_id} onChange={e => set("warehouse_id", e.target.value)} style={inputStyle}>
              <option value="">— Выберите —</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name} · {w.code}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Дата инвойса *</label>
            <input type="date" value={form.invoice_date} onChange={e => set("invoice_date", e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Номер инвойса *</label>
            <input data-testid="qa-invoice-input" value={form.invoice_number} onChange={e => set("invoice_number", e.target.value)} style={inputStyle} placeholder="INV-2026-04" />
          </div>
          <div>
            <label style={labelStyle}>Валюта</label>
            <select value={form.currency} onChange={e => set("currency", e.target.value)} style={inputStyle}>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="KZT">KZT</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Курс к ₸ {form.currency === "KZT" ? "(=1)" : "*"}</label>
            <input type="number" disabled={form.currency === "KZT"} value={form.currency === "KZT" ? 1 : form.exchange_rate_to_kzt}
                   onFocus={e => e.target.select()}
                   onChange={e => set("exchange_rate_to_kzt", e.target.value)} style={inputStyle} placeholder="540" />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <label style={labelStyle}>Закупочная цена за единицу ({form.currency})</label>            <input type="number" data-testid="qa-cost-input" value={form.cost_price_original}
                   onFocus={e => e.target.select()}
                   onChange={e => set("cost_price_original", e.target.value)} style={inputStyle} placeholder="850" />
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#8a8a89", marginTop: "4px" }}>
              В тенге за единицу: <b>{Math.round(unitCostKzt).toLocaleString("ru-RU")} ₸</b>
            </div>
          </div>
        </div>

        <div style={{ background: "#fafaf9", border: "1px solid #eee", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>Количество по размерам</div>
          {form.sizes_qty.map((row, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "6px" }}>
              <input value={row.size} onFocus={e => e.target.select()} onChange={e => updateSize(idx, "size", e.target.value)} style={inputStyle} placeholder="Размер" />
              <input type="number" min="0" data-testid={`qa-qty-${idx}`} value={row.qty || ""} onFocus={e => e.target.select()} onChange={e => updateSize(idx, "qty", e.target.value)} style={inputStyle} placeholder="Кол-во" />
            </div>
          ))}
          <button type="button" onClick={addSizeRow} style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: "4px", padding: "6px 10px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", color: "#6b7280" }}>+ Размер</button>
          <div style={{ marginTop: "8px", fontFamily: "Manrope", fontSize: "12px", color: "#374151" }}>
            Итого штук: <b>{totalQty}</b>
          </div>
        </div>

        <div style={{ background: "#fafaf9", border: "1px solid #eee", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>Дополнительные расходы на партию (в ₸)</div>
          {form.expenses.map((exp, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "8px", marginBottom: "6px" }}>
              <input value={exp.name} onChange={e => updateExp(idx, "name", e.target.value)} style={inputStyle} placeholder="Название (доставка, пошлина, брокер)" />
              <input type="number" min="0" value={exp.amount_kzt} onChange={e => updateExp(idx, "amount_kzt", e.target.value)} style={inputStyle} placeholder="Сумма ₸" />
            </div>
          ))}
          <button type="button" onClick={addExpRow} style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: "4px", padding: "6px 10px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", color: "#6b7280" }}>+ Расход</button>
        </div>

        <div style={{ background: "rgba(197,160,89,0.08)", border: "1px solid rgba(197,160,89,0.3)", borderRadius: "6px", padding: "10px 14px", marginBottom: "12px", fontFamily: "Manrope" }}>
          <div style={{ fontSize: "12px", color: "#8a6d2a", marginBottom: "2px" }}>ИТОГО ПАРТИЯ</div>
          <div style={{ fontSize: "22px", fontWeight: 700, color: "#8a6d2a" }}>{Math.round(arrivalTotalKzt).toLocaleString("ru-RU")} ₸</div>
          <div style={{ fontSize: "11px", color: "#8a8a89", marginTop: "2px" }}>
            Товар: {Math.round(unitCostKzt * totalQty).toLocaleString("ru-RU")} ₸ · Расходы: {Math.round(expensesTotalKzt).toLocaleString("ru-RU")} ₸
          </div>
        </div>

        <div>
          <label style={labelStyle}>Заметки</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
                    style={{ ...inputStyle, minHeight: "50px", fontFamily: "Manrope" }} placeholder="Комментарий к поступлению" />
        </div>

        {err && <div style={{ color: "#dc2626", fontSize: "12px", margin: "10px 0", fontFamily: "Manrope" }}>{err}</div>}

        <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px" }}>Отмена</button>
          <button data-testid="qa-submit-btn" onClick={submit} disabled={saving}
            style={{ flex: 2, background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "13px" }}>
            {saving ? "Проводим…" : "Провести поступление"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("visible"); // visible = published and in stock
  const [search, setSearch] = useState({ query: "", brand: "", category: "" });
  const [lookupRevision, setLookupRevision] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [sort, setSort] = useState("newest"); // newest | oldest — also persisted globally
  const [sortSaving, setSortSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkBrand, setBulkBrand] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkSubcategory, setBulkSubcategory] = useState("");
  const [showShareBuilder, setShowShareBuilder] = useState(false);
  const [bulkDescribe, setBulkDescribe] = useState(null);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  // Pagination — render only the first `pageSize` cards to keep the
  // initial paint under 100ms even when the catalog grows past a thousand.
  // Reset every time the filter changes so the counter is honest.
  const [pageSize, setPageSize] = useState(60);
  // Печать ценника прямо из списка — нужна всем сотрудникам, без открытия карточки.
  const [labelProduct, setLabelProduct] = useState(null);
  const [labelBusyId, setLabelBusyId] = useState(null);
  const available = useAvailableProducts({ enabled: filter === "visible", ...search, sort, pageSize, revision: lookupRevision });
  const updateSearch = (field, value) => {
    setSearch(prev => ({ ...prev, [field]: value }));
    setFilter("visible"); setPageSize(60); setSelectedIds(new Set());
  };
  const resetSearch = () => {
    setSearch({ query: "", brand: "", category: "" });
    setPageSize(60); setSelectedIds(new Set());
  };
  const changeFilter = value => {
    resetSearch(); setFilter(value);
  };

  // Если внутреннего артикула нет — присваиваем его на лету, иначе на ценнике
  // будет штрихкод из нулей.
  const openLabel = async (p) => {
    setLabelBusyId(p.id);
    let prod = p;
    if (!p.our_sku) {
      try {
        const r = await axios.post(`${API}/admin/products/${p.id}/generate-sku`, {},
          { withCredentials: true });
        prod = { ...p, our_sku: r.data?.our_sku || "" };
        setProducts(list => list.map(x => (x.id === p.id ? prod : x)));
        setLookupRevision(v => v + 1);
      } catch { /* печатаем как есть */ }
    }
    setLabelBusyId(null);
    setLabelProduct(prod);
  };

  // Load categories + brands once so ShareBuilder filters are prefilled.
  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data || [])).catch(() => {});
  }, []);

  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = (sortValue = sort) => {
    setLoading(true);
    setLoadError("");
    setLookupRevision(v => v + 1);
    axios.get(`${API}/products?include_hidden=true&limit=2000&sort=${sortValue}`, { withCredentials: true })
      .then(r => setProducts(r.data))
      .catch(() => setLoadError("Не удалось загрузить список товаров"))
      .finally(() => setLoading(false));
  };

  // On first mount: read the global setting → use it as initial sort.
  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then(r => {
        const s = r.data?.catalog_sort === "oldest" ? "oldest" : "newest";
        setSort(s);
        load(s);
      })
      .catch(() => load("newest"));
  }, []);

  // Cross-section deep-link: another admin page (e.g. Остатки / Поступления)
  // asked us to open the edit modal for a specific product. Consume the id
  // once the product list is loaded so we can hand a full object to the form.
  useEffect(() => {
    if (loading) return;
    let pid;
    try { pid = sessionStorage.getItem("adminEditProductId"); } catch { pid = null; }
    if (!pid) return;
    try { sessionStorage.removeItem("adminEditProductId"); } catch { /* noop */ }
    const p = products.find(x => x.id === pid);
    if (p) { setEditing(p); setShowForm(true); }
    else {
      // Product not in current filter/limit → fetch directly.
      axios.get(`${API}/products/${pid}`, { withCredentials: true })
        .then(r => { setEditing(r.data); setShowForm(true); })
        .catch(() => {});
    }
  }, [loading, products]);

  // Admin changes sort → persist globally + reload list.
  const handleSortChange = async (newSort) => {
    if (newSort === sort) return;
    setSort(newSort);
    setSortSaving(true);
    try {
      await axios.post(`${API}/admin/settings/catalog-sort`, { catalog_sort: newSort }, { withCredentials: true });
      load(newSort);
    } catch (e) {
      alert("Не удалось сохранить порядок сортировки");
    } finally {
      setSortSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить товар?")) return;
    await axios.delete(`${API}/products/${id}`, { withCredentials: true });
    load();
  };

  const toggleVisibility = async (p) => {
    const newVal = !p.is_active;
    // optimistic
    setProducts(prev => prev.map(x => x.id === p.id ? { ...x, is_active: newVal } : x));
    try {
      await axios.post(`${API}/admin/products/${p.id}/visibility`, { is_active: newVal }, { withCredentials: true });
      setLookupRevision(v => v + 1);
      setSelectedIds(prev => { const next = new Set(prev); next.delete(p.id); return next; });
    } catch (e) {
      // revert
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, is_active: !newVal } : x));
      alert("Не удалось изменить видимость");
    }
  };

  const visibleCount = available.available_count ?? products.filter(p => p.is_active && p.in_stock === true
    && typeof p.images?.[0] === "string" && p.images[0].trim()).length;
  const hiddenCount = products.filter(p => !p.is_active).length;
  const noImagesCount = products.filter(p => !p.images || p.images.length === 0).length;

  const shown = products.filter(p => {
    if (filter === "visible") return p.is_active && p.images?.length > 0;
    if (filter === "hidden") return !p.is_active;
    if (filter === "no-images") return !p.images || p.images.length === 0;
    return true;
  });
  const shownPage = filter === "visible" ? available.items : shown.slice(0, pageSize);
  const shownTotal = filter === "visible" ? available.total : shown.length;
  const listingLoading = filter === "visible" ? available.loading : loading;
  const listingError = filter === "visible" ? available.error : loadError;
  // Reset the paginator when the filter changes.
  useEffect(() => { setPageSize(60); }, [filter]);

  const shownIds = filter === "visible" ? available.matching_ids : shown.map(p => p.id);
  const allShownSelected = shownIds.length > 0 && shownIds.every(id => selectedIds.has(id));
  const toggleAllShown = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allShownSelected) shownIds.forEach(id => next.delete(id));
      else shownIds.forEach(id => next.add(id));
      return next;
    });
  };

  const openBulkDescribe = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (ids.length > 40 && !window.confirm(
      `Выбрано ${ids.length} товаров. Генерация идёт примерно по 10 секунд на товар — это займёт около ${Math.ceil(ids.length * 10 / 60)} мин. Продолжить?`)) return;
    setBulkBusy(true);
    try {
      const pool = new Map([...products, ...(available.items || [])].map(p => [p.id, p]));
      for (const id of ids.filter(i => !pool.has(i))) {
        try {
          const r = await axios.get(`${API}/products/${id}`);
          pool.set(id, r.data);
        } catch { /* товар мог быть удалён — пропускаем */ }
      }
      setBulkDescribe(ids.map(id => pool.get(id)).filter(Boolean));
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`УДАЛИТЬ НАВСЕГДА ${ids.length} выбранных товаров?\n\nТовары будут полностью удалены из каталога. Действие необратимо.`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/admin/products/bulk-delete`, { ids }, { withCredentials: true });
      window.alert(`Удалено товаров: ${r.data.deleted_count}`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Ошибка массового удаления");
    } finally { setBulkBusy(false); }
  };

  const bulkSetBrand = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !bulkBrand) return;
    if (!window.confirm(`Проставить бренд «${bulkBrand}» выбранным товарам (${ids.length})?\n\nТовары, у которых бренд уже указан, останутся без изменений.`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/admin/products/bulk-set-brand`,
        { ids, brand: bulkBrand, only_empty: true }, { withCredentials: true });
      const skipped = r.data.skipped_count
        ? `\nПропущено (бренд уже был указан): ${r.data.skipped_count}` : "";
      window.alert(`Бренд «${r.data.brand}» проставлен товарам: ${r.data.updated_count}${skipped}`);
      setSelectedIds(new Set());
      setBulkBrand("");
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Не удалось проставить бренд");
    } finally { setBulkBusy(false); }
  };

  const bulkSubOptions = (categories.find(c => c.name === bulkCategory)?.subcategories) || [];

  const bulkSetCategory = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !bulkCategory) return;
    const where = bulkSubcategory ? `${bulkCategory} › ${bulkSubcategory}` : bulkCategory;
    if (!window.confirm(`Перенести ${ids.length} выбранных товаров в «${where}»?\n\nТекущая категория у этих товаров будет заменена.`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/admin/products/bulk-set-category`,
        { ids, category: bulkCategory, subcategory: bulkSubcategory }, { withCredentials: true });
      window.alert(`Перенесено товаров: ${r.data.updated_count} → ${r.data.category}${r.data.subcategory ? " › " + r.data.subcategory : ""}`);
      setSelectedIds(new Set());
      setBulkCategory("");
      setBulkSubcategory("");
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Не удалось сменить категорию");
    } finally { setBulkBusy(false); }
  };

  const filterBtn = (key, label, count, color) => (
    <button
      key={key}
      data-testid={`filter-${key}`}
      onClick={() => changeFilter(key)}
      aria-pressed={filter === key}
      style={{
        background: filter === key ? color : "white",
        color: filter === key ? "white" : "#374151",
        border: `1px solid ${filter === key ? color : "#e5e7eb"}`,
        borderRadius: "6px", padding: "7px 12px", cursor: "pointer",
        fontFamily: "Manrope", fontSize: "12px", fontWeight: 600,
        display: "inline-flex", alignItems: "center", gap: "6px",
      }}
    >{label}<span data-testid={`filter-count-${key}`} style={{ background: filter === key ? "rgba(255,255,255,0.25)" : "#f3f4f6", padding: "1px 7px", borderRadius: "8px", fontSize: "11px" }}>{count}</span></button>
  );

  return (
    <div className="admin-products-list" data-testid="admin-products-list">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {filterBtn("all", "Все", products.length, "#374151")}
          {filterBtn("visible", "Видимые · в наличии", visibleCount, "#16a34a")}
          {filterBtn("hidden", "Скрытые", hiddenCount, "#64748b")}
          {filterBtn("no-images", "Без фото", noImagesCount, "#dc2626")}
          <button data-testid="select-all-products-btn" onClick={toggleAllShown}
            disabled={listingLoading || !!listingError || shownIds.length === 0}
            title="Выделить все товары в текущем фильтре"
            style={{ background: "white", color: "#374151", border: "1px dashed #cbd5e1",
                     borderRadius: "6px", padding: "7px 12px", cursor: "pointer",
                     fontFamily: "Manrope", fontSize: "12px", fontWeight: 600 }}>
            {allShownSelected ? "Снять выделение" : "Выделить все"}
          </button>
          <button data-testid="open-share-builder" onClick={() => setShowShareBuilder(true)}
            title="Собрать подборку из 5 товаров для отправки в WhatsApp"
            style={{ background: "linear-gradient(135deg,#25D366,#128C7E)",
                     color: "white", border: "none", borderRadius: "6px",
                     padding: "7px 12px", cursor: "pointer",
                     fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                     display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Share2 size={13} /> Подборка WhatsApp
          </button>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: "Manrope", fontSize: "12px", color: "#374151", fontWeight: 600 }}>
            Порядок:
            <select
              data-testid="catalog-sort-select"
              value={sort}
              onChange={(e) => handleSortChange(e.target.value)}
              disabled={sortSaving}
              style={{
                border: "1px solid #e5e7eb", borderRadius: "6px", padding: "7px 28px 7px 10px",
                fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, color: "#111",
                background: "white", cursor: "pointer", outline: "none",
                appearance: "none", WebkitAppearance: "none",
                backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1L5 5L9 1' stroke='%23374151' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
              }}
            >
              <option value="newest">Сначала новые</option>
              <option value="oldest">Сначала старые</option>
            </select>
          </label>
          <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "#94a3b8" }}>
            (применяется и для клиентов)
          </span>
          <HelpVideoButton slug="video-product-card" label="Как заполнить карточку"
            testId="help-video-product-card" />
          <button data-testid="add-product-btn" onClick={() => { setEditing(null); setShowForm(true); }}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 20px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
            <Plus size={16} />Добавить товар
          </button>
        </div>
      </div>

      <ProductQuickSearch {...search} brands={available.brands} categories={available.categories}
        noBrandCount={available.no_brand_count}
        onChange={updateSearch} onReset={resetSearch} active={filter === "visible"}
        count={available.total} loading={available.loading} error={available.error}
        onReload={() => setLookupRevision(v => v + 1)} />

      {filter !== "visible" && loadError && <div role="alert" data-testid="products-load-error" className="product-search-error">
        {loadError}<button data-testid="products-reload" onClick={() => load()}>Повторить</button>
      </div>}
      {listingLoading ? (
        <div data-testid="products-loading" style={{ textAlign: "center", padding: "40px", color: "#888", fontFamily: "Manrope" }}>Загрузка...</div>
      ) : (
        <>
        {!listingError && shownTotal === 0 && <div className="product-grid-empty" data-testid="products-empty-state">
          {filter === "visible" ? "Опубликованных товаров в наличии по этим условиям не найдено." : "В этом разделе пока нет товаров."}
        </div>}
        <div data-testid="admin-products-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,280px),1fr))", gap: "16px" }}>
          {shownPage.map(p => {
            const hasImage = p.images?.[0];
            const isHidden = !p.is_active;
            const isChecked = selectedIds.has(p.id);
            return (
              <div key={p.id} data-testid={`admin-product-${p.id}`} style={{
                background: "white", border: `1px solid ${isChecked ? "#C5A059" : (isHidden ? "#e2e8f0" : "#e5e7eb")}`,
                boxShadow: isChecked ? "0 0 0 1px #C5A059" : "none",
                borderRadius: "8px", overflow: "hidden", position: "relative", minWidth: 0,
                opacity: isHidden && !isChecked ? 0.55 : 1,
              }}>
                {/* Bulk-select checkbox */}
                <label style={{ position: "absolute", top: "8px", right: "8px", zIndex: 3,
                                background: "rgba(255,255,255,0.92)", borderRadius: "5px",
                                padding: "5px", display: "flex", cursor: "pointer",
                                border: "1px solid #e5e7eb" }}
                       onClick={e => e.stopPropagation()}>
                  <input data-testid={`select-product-${p.id}`} type="checkbox" checked={isChecked}
                    onChange={() => toggleId(p.id)}
                    style={{ accentColor: "#C5A059", width: "16px", height: "16px", cursor: "pointer" }} />
                </label>
                {/* Status ribbon */}
                {isHidden && (
                  <div data-testid={`hidden-ribbon-${p.id}`} style={{ position: "absolute", top: "8px", left: "8px", background: "rgba(15,23,42,0.85)", color: "white", padding: "3px 8px", borderRadius: "3px", fontFamily: "Manrope", fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", zIndex: 2 }}>Скрыт</div>
                )}
                {!hasImage && (
                  <div data-testid={`product-no-image-${p.id}`} style={{ position: "absolute", top: "8px", right: "44px", background: "rgba(220,38,38,0.95)", color: "white", padding: "3px 8px", borderRadius: "3px", fontFamily: "Manrope", fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", zIndex: 2, display: "inline-flex", alignItems: "center", gap: "3px" }}><ImageOff size={10} />Нет фото</div>
                )}
                {hasImage
                  ? <img src={resolveImageUrl(p.images[0], { w: 400 })} alt={p.name}
                          loading="lazy" decoding="async"
                          style={{ width: "100%", height: "160px", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "160px", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}><ImageOff size={28} /></div>}
                <div style={{ padding: "14px" }}>
                  <div data-testid={`product-card-category-${p.id}`} style={{ fontFamily: "Manrope", fontSize: "10px", color: "#8c733f", textTransform: "uppercase", marginBottom: "4px" }}>{[p.category, p.brand].filter(Boolean).join(" · ")}</div>
                  <div data-testid={`product-card-name-${p.id}`} style={{ fontFamily: "Cormorant Garamond", fontSize: "17px", color: "#0A0A0A", marginBottom: "8px" }}>{p.name}</div>
                  <ProductArticles product={p} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                    <span data-testid={`product-card-price-${p.id}`} style={{ fontFamily: "Manrope", fontSize: "15px", fontWeight: 700, color: "#C5A059" }}>{Number(p.price || 0).toLocaleString("ru-RU")} ₸</span>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <button
                        data-testid={`toggle-visibility-${p.id}`}
                        onClick={() => toggleVisibility(p)}
                        title={p.is_active ? "Скрыть от клиента" : "Показать клиенту"}
                        style={{
                          background: p.is_active ? "#ecfdf5" : "#f1f5f9",
                          border: `1px solid ${p.is_active ? "#a7f3d0" : "#cbd5e1"}`,
                          color: p.is_active ? "#059669" : "#475569",
                          borderRadius: "4px", padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center",
                        }}>
                        {p.is_active ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                      <button data-testid={`print-label-${p.id}`}
                        onClick={() => openLabel(p)} disabled={labelBusyId === p.id}
                        title="Напечатать ценник"
                        style={{ background: "#FDFBF7", border: "1px solid #C5A059", borderRadius: "4px", padding: "6px 10px", cursor: labelBusyId === p.id ? "wait" : "pointer", color: "#8a6d2a", display: "flex", alignItems: "center" }}>
                        <Printer size={13} />
                      </button>
                      <button data-testid={`edit-product-${p.id}`} onClick={() => { setEditing(p); setShowForm(true); }} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "6px 10px", cursor: "pointer", color: "#374151", display: "flex", alignItems: "center" }}><Edit2 size={13} /></button>
                      <CopyLinkButton
                        url={productShareUrl(p.id)}
                        label=""
                        copiedLabel=""
                        compact
                        testid={`copy-product-link-${p.id}`}
                      />
                      <button data-testid={`delete-product-${p.id}`} title="Удалить товар" aria-label="Удалить товар" onClick={() => handleDelete(p.id)} style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "6px 10px", cursor: "pointer", color: "#dc2626", display: "flex", alignItems: "center" }}><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {shownTotal > pageSize && !listingError && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <button data-testid="products-show-more"
              onClick={() => setPageSize(v => v + 60)}
              style={{ background: "white", border: "1px solid #C5A059", color: "#8a6d2a",
                        borderRadius: 6, padding: "10px 22px", cursor: "pointer",
                        fontFamily: "Manrope", fontSize: 13, fontWeight: 700 }}>
              Показать ещё · {Math.min(60, shownTotal - pageSize)} из {shownTotal - pageSize}
            </button>
          </div>
        )}
        </>
      )}

      {showForm && <ProductForm product={editing} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}

      {labelProduct && (
        <LabelPrintModal product={labelProduct} onClose={() => setLabelProduct(null)} />
      )}

      {/* Sticky bulk-action bar */}
      {selectedIds.size > 0 && (
        <div data-testid="products-bulk-bar"
          style={{ position: "fixed", left: "50%", bottom: "24px", transform: "translateX(-50%)",
                    zIndex: 90, background: "#0A0A0A", color: "white",
                    borderRadius: "12px", padding: "12px 18px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.35), 0 0 0 1px #C5A059",
                    display: "flex", alignItems: "center", flexWrap: "wrap", width: "max-content", maxWidth: "calc(100% - 24px)", gap: "14px", fontFamily: "Manrope" }}>
          <div data-testid="products-selected-count" style={{ fontSize: "13px", fontWeight: 600 }}>
            Выбрано: <span style={{ color: "#D4B878" }}>{selectedIds.size}</span>
          </div>
          <div style={{ width: "1px", height: "22px", background: "#333" }} />
          <button data-testid="products-bulk-describe-btn" disabled={bulkBusy}
            onClick={openBulkDescribe}
            style={{ background: "linear-gradient(135deg,#C5A059,#A5803C)", color: "#1A1A1A", border: "none",
                      borderRadius: "6px", padding: "8px 14px", cursor: bulkBusy ? "wait" : "pointer",
                      fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Sparkles size={13} /> Описания по фото
          </button>
          <div style={{ width: "1px", height: "22px", background: "#333" }} />
          {/* Массовая привязка бренда товарам без бренда */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <select data-testid="products-bulk-brand-select" value={bulkBrand} disabled={bulkBusy}
              onChange={(e) => setBulkBrand(e.target.value)}
              style={{ background: "#1A1A1A", color: "white", border: "1px solid #333", borderRadius: "6px",
                        padding: "8px 10px", fontSize: "12px", fontFamily: "Manrope", maxWidth: "190px" }}>
              <option value="">Выберите бренд…</option>
              {brands.map(b => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}
            </select>
            <button data-testid="products-bulk-brand-btn" onClick={bulkSetBrand} disabled={bulkBusy || !bulkBrand}
              title="Проставит бренд только тем выбранным товарам, у которых бренда нет"
              style={{ background: bulkBrand ? "linear-gradient(135deg,#C5A059,#A5803C)" : "#2A2A2A",
                        color: bulkBrand ? "#1A1A1A" : "#6B7280", border: "none",
                        borderRadius: "6px", padding: "8px 14px",
                        cursor: bulkBusy ? "wait" : (bulkBrand ? "pointer" : "not-allowed"),
                        fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                        display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Tag size={13} /> {bulkBusy ? "Сохраняем…" : "Проставить бренд"}
            </button>
          </div>
          <div style={{ width: "1px", height: "22px", background: "#333" }} />
          {/* Массовый перенос в категорию / подкатегорию */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <select data-testid="products-bulk-category-select" value={bulkCategory} disabled={bulkBusy}
              onChange={(e) => { setBulkCategory(e.target.value); setBulkSubcategory(""); }}
              style={{ background: "#1A1A1A", color: "white", border: "1px solid #333", borderRadius: "6px",
                        padding: "8px 10px", fontSize: "12px", fontFamily: "Manrope", maxWidth: "190px" }}>
              <option value="">Категория…</option>
              {categories.map(c => <option key={c.id || c.slug} value={c.name}>{c.name}</option>)}
            </select>
            {bulkSubOptions.length > 0 && (
              <select data-testid="products-bulk-subcategory-select" value={bulkSubcategory} disabled={bulkBusy}
                onChange={(e) => setBulkSubcategory(e.target.value)}
                style={{ background: "#1A1A1A", color: "white", border: "1px solid #333", borderRadius: "6px",
                          padding: "8px 10px", fontSize: "12px", fontFamily: "Manrope", maxWidth: "170px" }}>
                <option value="">Без подкатегории</option>
                {bulkSubOptions.map(s => <option key={s.id || s.slug} value={s.name}>{s.name}</option>)}
              </select>
            )}
            <button data-testid="products-bulk-category-btn" onClick={bulkSetCategory} disabled={bulkBusy || !bulkCategory}
              title="Заменит категорию у всех выбранных товаров"
              style={{ background: bulkCategory ? "linear-gradient(135deg,#C5A059,#A5803C)" : "#2A2A2A",
                        color: bulkCategory ? "#1A1A1A" : "#6B7280", border: "none",
                        borderRadius: "6px", padding: "8px 14px",
                        cursor: bulkBusy ? "wait" : (bulkCategory ? "pointer" : "not-allowed"),
                        fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                        display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <FolderTree size={13} /> {bulkBusy ? "Сохраняем…" : "Сменить категорию"}
            </button>
          </div>
          <div style={{ width: "1px", height: "22px", background: "#333" }} />
          <button data-testid="products-bulk-delete-btn" onClick={bulkDelete} disabled={bulkBusy}
            style={{ background: "linear-gradient(135deg,#DC2626,#B91C1C)", color: "white", border: "none",
                      borderRadius: "6px", padding: "8px 14px", cursor: bulkBusy ? "wait" : "pointer",
                      fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Trash2 size={13} /> {bulkBusy ? "Удаляем…" : "Удалить выбранные"}
          </button>
          <button data-testid="products-bulk-cancel-btn" onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}
            style={{ background: "transparent", color: "#9CA3AF", border: "none",
                      cursor: bulkBusy ? "wait" : "pointer", fontSize: "11px", padding: "6px 8px" }}>
            Отмена
          </button>
        </div>
      )}
      {bulkDescribe && (
        <BulkDescribeModal
          products={bulkDescribe}
          onClose={() => setBulkDescribe(null)}
          onSaved={() => { setSelectedIds(new Set()); setLookupRevision(r => r + 1); load(); }}
        />
      )}
      {showShareBuilder && (
        <ShareBuilder
          categories={categories}
          brands={brands}
          onClose={() => setShowShareBuilder(false)}
        />
      )}
    </div>
  );
}
